/**
 * G16 ingest on the user's device: project Settings › Sources (sealed `ingestcfg` role config),
 * the owning daemons' status records, and the Import review queue of daemon-written `ingest`
 * items (imports, drift, conflicts, insights).
 *
 * Every entity has one writer role. This device writes only the config and decision records and —
 * on accept — exactly the entity the item names (deterministic id, so a retried accept never
 * mints a second one). The daemon folds decisions into its items; the queue shows a decision as
 * soon as it is written.
 */
import {
  defaultIngestConfig,
  GoalRecord,
  goalEntityId,
  IngestCfgEntity,
  type IngestConfigRecord,
  IngestConfigRecord as IngestConfigSchema,
  type IngestDecisionRecord,
  type IngestEdits,
  IngestItemRecord,
  type IngestState,
  type IngestStatusRecord,
  ingestConfigId,
  ingestDecisionId,
  ingestEntityId,
  ingestResearchSources,
  ingestRuleRecord,
  ingestTargetKind,
  ingestTaskRecord,
  ingestWorkstreamRecord,
  PrivateTaskRecord,
  ResearchRecord,
} from '@viko/protocol'

type Kind = 'ingestcfg' | 'ingest' | 'task' | 'workstream' | 'rule' | 'research' | 'goal'
interface Row {
  id: string
  value: Record<string, unknown>
}
/** The slice of BrowserEntities this module needs (sealed, authenticated, E2E). */
export interface IngestEntityStore {
  list(kind: Kind): Promise<Row[]>
  read(kind: Kind, id: string): Promise<Row>
  write(kind: Kind, id: string, value: Record<string, unknown>): Promise<unknown>
}

export interface ItemView {
  id: string
  item: IngestItemRecord
  decision?: IngestDecisionRecord
  /** The decision (if any) wins until the daemon folds it in. */
  state: IngestState
}

export interface IngestView {
  config: IngestConfigRecord
  configSaved: boolean
  /** One per machine syncing this project. */
  status: IngestStatusRecord[]
  items: ItemView[]
}

const CATEGORY_RANK = { conflict: 0, drift: 1, import: 2, insight: 3 } as const

/** Review order: needs-you first, conflicts before drift before imports, then confidence, recency. */
export function sortItems(rows: ItemView[]): ItemView[] {
  const rank = (v: ItemView) => (v.state === 'pending' ? 0 : 1)
  return [...rows].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      CATEGORY_RANK[a.item.category] - CATEGORY_RANK[b.item.category] ||
      b.item.confidence - a.item.confidence ||
      b.item.updatedAt - a.item.updatedAt ||
      a.id.localeCompare(b.id),
  )
}

export async function loadIngest(store: IngestEntityStore, projectId: string): Promise<IngestView> {
  const [cfgRows, itemRows] = await Promise.all([store.list('ingestcfg'), store.list('ingest')])
  let config: IngestConfigRecord | undefined
  const status: IngestStatusRecord[] = []
  const decisions = new Map<string, IngestDecisionRecord>()
  for (const row of cfgRows) {
    const parsed = IngestCfgEntity.safeParse(row.value)
    if (!parsed.success || parsed.data.projectId !== projectId) continue
    const v = parsed.data
    if (v.role === 'config') {
      if (row.id === ingestConfigId(projectId)) config = v
    } else if (v.role === 'status') status.push(v)
    else if (row.id === ingestDecisionId(v.itemId)) decisions.set(v.itemId, v)
  }
  const items: ItemView[] = []
  for (const row of itemRows) {
    const parsed = IngestItemRecord.safeParse(row.value)
    if (!parsed.success || parsed.data.projectId !== projectId) continue
    const item = parsed.data
    const decision = decisions.get(row.id)
    items.push({
      id: row.id,
      item,
      ...(decision ? { decision } : {}),
      state:
        item.state === 'pending' && decision
          ? decision.action === 'accept'
            ? 'accepted'
            : 'rejected'
          : item.state,
    })
  }
  return {
    config: config ?? defaultIngestConfig(projectId),
    configSaved: !!config,
    status: status.sort((a, b) => a.machineId.localeCompare(b.machineId)),
    items: sortItems(items),
  }
}

async function readOrNull(store: IngestEntityStore, kind: Kind, id: string) {
  try {
    return await store.read(kind, id)
  } catch {
    return null
  }
}

/** Read-modify-write of the project's config (this device is its only writer role). */
export async function saveIngestConfig(
  store: IngestEntityStore,
  projectId: string,
  mutate: (cfg: IngestConfigRecord) => IngestConfigRecord,
  now = Date.now(),
): Promise<IngestConfigRecord> {
  const id = ingestConfigId(projectId)
  const current = await readOrNull(store, 'ingestcfg', id)
  const base = current ? IngestConfigSchema.parse(current.value) : defaultIngestConfig(projectId)
  if (base.projectId !== projectId) throw new Error('Ingest settings belong to another project')
  const next = IngestConfigSchema.parse({
    ...mutate(base),
    role: 'config',
    projectId,
    updatedAt: now,
  })
  await store.write('ingestcfg', id, next)
  return next
}

/** One glob per line; blank lines dropped; invalid lines reported, not silently kept. */
export function parseExclusions(text: string): { ok: string[]; bad: string[] } {
  const ok: string[] = []
  const bad: string[] = []
  for (const raw of text.split('\n')) {
    const g = raw.trim()
    if (!g) continue
    if (g.length <= 200 && !g.includes('\0') && !g.startsWith('..') && !g.includes('/../'))
      ok.includes(g) || ok.push(g)
    else bad.push(g)
  }
  return { ok: ok.slice(0, 100), bad }
}

export type IngestAction = 'accept' | 'reject'

/** What accepting an item will do, in words (shown on the button / in the drill-down). */
export function acceptLabel(v: ItemView, edits?: IngestEdits): string {
  const i = v.item
  if (i.category === 'conflict') return 'Use the source version'
  if (i.match?.relation === 'completes') return 'Mark task done'
  switch (ingestTargetKind(i, edits)) {
    case 'task':
      return 'Create task'
    case 'milestone':
      return 'Add milestone'
    case 'goal':
      return 'Set as goal'
    case 'workstream':
      return 'Track as workstream'
    case 'rule':
      return 'Keep as rule'
    case 'research':
      return 'Save to research'
    default:
      return 'Acknowledge'
  }
}

async function taskOf(store: IngestEntityStore, id: string) {
  const row = await store.read('task', id)
  return PrivateTaskRecord.parse({ ...row.value, id })
}
const bodyOf = (t: ReturnType<typeof PrivateTaskRecord.parse>) => {
  const { id: _id, ...rest } = t
  return rest
}
const TASK_STATUS = {
  open: 'ready',
  'in-progress': 'ready',
  blocked: 'blocked',
  done: 'done',
} as const

/** Apply one accepted item; returns the entity written (if any). */
async function applyAccept(
  store: IngestEntityStore,
  projectId: string,
  v: ItemView,
  edits: IngestEdits | undefined,
  now: number,
): Promise<Pick<IngestDecisionRecord, 'entityKind' | 'entityId'>> {
  const i = v.item
  // Conflict: the source version replaces the Viko fields it disagrees on.
  if (i.category === 'conflict' && i.match?.kind === 'task') {
    const t = await taskOf(store, i.match.id)
    if (t.projectId !== projectId) throw new Error('Task belongs to another project')
    const next = { ...bodyOf(t), updatedAt: now }
    for (const c of i.conflicts ?? []) {
      if (c.field === 'title') next.title = edits?.title ?? c.source
      if (c.field === 'status' && c.source in TASK_STATUS) {
        next.status = TASK_STATUS[c.source as keyof typeof TASK_STATUS]
        if (next.status === 'done' && t.status !== 'done') next.landedAt = now
      }
    }
    await store.write('task', t.id, next)
    return { entityKind: 'task', entityId: t.id }
  }
  // Evidence says an existing task is complete.
  if (i.match?.relation === 'completes' && i.match.kind === 'task') {
    const t = await taskOf(store, i.match.id)
    if (t.projectId !== projectId) throw new Error('Task belongs to another project')
    if (t.status !== 'done')
      await store.write('task', t.id, {
        ...bodyOf(t),
        status: 'done',
        landedAt: now,
        blockedReason: null,
        updatedAt: now,
      })
    return { entityKind: 'task', entityId: t.id }
  }
  const target = ingestTargetKind(i, edits)
  if (target === 'task') {
    const id = ingestEntityId(v.id, 'task')
    if (!(await readOrNull(store, 'task', id)))
      await store.write('task', id, ingestTaskRecord(v.id, i, edits, now))
    return { entityKind: 'task', entityId: id }
  }
  if (target === 'workstream') {
    const id = ingestEntityId(v.id, 'workstream')
    if (!(await readOrNull(store, 'workstream', id)))
      await store.write('workstream', id, ingestWorkstreamRecord(v.id, i, edits, now))
    return { entityKind: 'workstream', entityId: id }
  }
  if (target === 'rule') {
    const id = ingestEntityId(v.id, 'rule')
    if (!(await readOrNull(store, 'rule', id)))
      await store.write('rule', id, ingestRuleRecord(v.id, i, edits, now))
    return { entityKind: 'rule', entityId: id }
  }
  if (target === 'research') {
    const id = ingestEntityId(v.id, 'research')
    if (!(await readOrNull(store, 'research', id)))
      await store.write(
        'research',
        id,
        ResearchRecord.parse({
          v: 1,
          scope: 'project',
          projectId,
          title: (edits?.title ?? i.title).slice(0, 200),
          summary: (i.detail ?? i.title).slice(0, 4000),
          sources: ingestResearchSources(i),
          tags: ['imported'],
          by: 'user',
          createdAt: now,
          updatedAt: now,
        }),
      )
    return { entityKind: 'research', entityId: id }
  }
  if (target === 'goal' || target === 'milestone') {
    const id = goalEntityId(projectId)
    const row = await readOrNull(store, 'goal', id)
    const cur = row ? GoalRecord.safeParse(row.value) : undefined
    const title = (edits?.title ?? i.title).slice(0, 200)
    const goal = cur?.success ? cur.data : undefined
    const milestone = { id: ingestEntityId(v.id, 'milestone').slice(0, 64), title, criteria: [] }
    const next = GoalRecord.parse(
      goal
        ? {
            ...goal,
            // Never overwrite the user's goal text: an imported goal joins as a milestone.
            milestones: goal.milestones.some((m) => m.id === milestone.id)
              ? goal.milestones
              : [...goal.milestones, milestone].slice(0, 16),
            revision: goal.revision + 1,
            updatedAt: now,
          }
        : {
            projectId,
            text: target === 'goal' ? (edits?.title ?? i.title) : 'Imported from the project docs',
            status: 'active',
            milestones: target === 'milestone' ? [milestone] : [],
            revision: 1,
            updatedAt: now,
          },
    )
    await store.write('goal', id, next)
    return { entityKind: 'goal', entityId: id }
  }
  return {}
}

/**
 * Batch decisions ("Accept all", per item, or with edits). Only items still pending (and in this
 * project) are decided; each accept re-reads the item so it applies exactly what the daemon holds.
 */
export async function decideIngest(
  store: IngestEntityStore,
  projectId: string,
  selected: ItemView[],
  action: IngestAction,
  edits: Record<string, IngestEdits> = {},
  now = Date.now(),
): Promise<{ decided: string[]; skipped: string[] }> {
  const decided: string[] = []
  const skipped: string[] = []
  for (const v of sortItems(selected)) {
    if (v.state !== 'pending' || v.item.projectId !== projectId) {
      skipped.push(v.id)
      continue
    }
    const fresh = IngestItemRecord.parse((await store.read('ingest', v.id)).value)
    if (fresh.state !== 'pending' || fresh.projectId !== projectId) {
      skipped.push(v.id)
      continue
    }
    const e = edits[v.id]
    const applied =
      action === 'accept' ? await applyAccept(store, projectId, { ...v, item: fresh }, e, now) : {}
    const id = ingestDecisionId(v.id)
    await readOrNull(store, 'ingestcfg', id)
    await store.write('ingestcfg', id, {
      role: 'decision',
      projectId,
      itemId: v.id,
      action,
      ...(e ? { edits: e } : {}),
      ...applied,
      at: now,
    })
    decided.push(v.id)
  }
  return { decided, skipped }
}
